import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCourses } from '../services/courseService';
import { getAllCategories } from '../services/categoryService';
import { resolveMediaUrl } from '../utils/media';
import './Courses.css';

function Courses() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch courses and categories in parallel
      const [coursesResponse, categoriesResponse] = await Promise.all([
        getAllCourses({ limit: 6, isActive: true, isFeatured: true }),
        getAllCategories({ isActive: true, sortBy: 'order', sortOrder: 'asc' })
      ]);

      if (coursesResponse.success) {
        setCourses(coursesResponse.data);
      }

      if (categoriesResponse.success) {
        // Add "All Courses" option
        setCategories([
          { _id: 'all', name: 'All Courses', slug: 'all' },
          ...categoriesResponse.data
        ]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = activeCategory === 'all' 
    ? courses 
    : courses.filter(course => course.category?._id === activeCategory);

  // Helper function to get category color
  const getCategoryColor = (category) => {
    return category?.color || '#667eea';
  };

  // Helper function to format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCourseImage = (course) => resolveMediaUrl(course.image);

  if (loading) {
    return (
      <section className="courses">
        <div className="container">
          <div className="courses-header">
            <h2 className="section-title">
              Explore Our Popular <span className="highlight">Courses</span>
            </h2>
            <p className="section-subtitle">Loading courses...</p>
          </div>
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="courses">
        <div className="container">
          <div className="courses-header">
            <h2 className="section-title">
              Explore Our Popular <span className="highlight">Courses</span>
            </h2>
            <p className="section-subtitle error-message">
              {error}
            </p>
          </div>
          <button className="retry-btn" onClick={fetchData}>
            Try Again
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="courses">
      <div className="container">
        <div className="courses-header">
          <h2 className="section-title">
            Explore Our Popular <span className="highlight">Courses</span>
          </h2>
          <p className="section-subtitle">
            Choose from hundreds of courses from specialist organizations
          </p>
        </div>

        {categories.length > 0 && (
          <div className="category-filter">
            {categories.map(category => (
              <button
                key={category._id}
                className={`filter-btn ${activeCategory === category._id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category._id)}
                style={activeCategory === category._id ? {
                  backgroundColor: category.color || '#667eea',
                  borderColor: category.color || '#667eea'
                } : {}}
              >
                {category.icon && <span className="category-icon">{category.icon}</span>}
                {category.name}
              </button>
            ))}
          </div>
        )}

        <div className="courses-grid">
          {filteredCourses.length > 0 ? (
            filteredCourses.map(course => (
              <div key={course._id} className="course-card" onClick={() => navigate(`/courses/${course.path}`)} style={{ cursor: 'pointer' }}>
                <div 
                  className="course-image" 
                  style={{ 
                    background: `linear-gradient(135deg, ${getCategoryColor(course.category)} 0%, ${getCategoryColor(course.category)}dd 100%)` 
                  }}
                >
                  {getCourseImage(course) ? (
                    <img
                      src={getCourseImage(course)}
                      alt={course.title}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="course-emoji">{course.category?.icon || '📚'}</span>
                  )}
                  <div className="course-badge">{course.level}</div>
                </div>
                
                <div className="course-content">
                  <div className="course-meta">
                    <span className="course-category">
                      {course.category?.name || 'General'}
                    </span>
                    <div className="course-rating">
                      <span className="star">⭐</span>
                      <span className="rating-value">{course.rating || 0}</span>
                    </div>
                  </div>

                  <h3 className="course-title">{course.title}</h3>
                  
                  <div className="course-instructor">
                    <div className="instructor-avatar">
                      {course.instructor?.charAt(0) || 'I'}
                    </div>
                    <span className="instructor-name">{course.instructor}</span>
                  </div>

                  <div className="course-stats">
                    <div className="stat">
                      <span className="stat-icon">⏱️</span>
                      <span className="stat-value">{course.duration}</span>
                    </div>
                  </div>

                  <div className="course-footer">
                    <div className="course-price">
                      {formatPrice(course.price)}
                      {course.originalPrice && course.originalPrice > course.price && (
                        <span className="original-price">
                          {formatPrice(course.originalPrice)}
                        </span>
                      )}
                    </div>
                    <button className="enroll-btn" onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.path}`); }}>View Course</button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="no-courses">
              <p>No courses found in this category.</p>
            </div>
          )}
        </div>

        <div className="view-all-container">
          <button className="view-all-btn" onClick={() => navigate('/courses')}>
            View All Courses →
          </button>
        </div>
      </div>
    </section>
  );
}

export default Courses;
